import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    validateAmount,
    validateInputAmount,
    useJunoCommonFunctions,
    validateCurrency,
} from '../../../../client/Juno/helpers';
import { apiService } from '../../../../client/common/apiCallService';
import TransferForm from './TransferForm';
import TransferModal from './TransferModal';
import TransactionStatus from '../../TransactionStatus';
import { getClientTransactionUrl } from '../../../../client/Juno/helpers';
import { isEmptyObject } from '../../../helpers/constant';
import JunoButton from '../../common/JunoButton';
import clsx from 'clsx';

const TransferTransaction = props => {
    const { open: propOpen, onClose: onCloseModal, selectedMerchant, selectedBalance = {} } = props;
    // Modal state management
    const [open, setOpen] = useState(false);
    const { balancesCurrencies = [], clientData } = useSelector(state => state.juno);

    useEffect(() => {
        if (balancesCurrencies) {
            propOpen ? setOpen(true) : setOpen(false);
        }
    }, [propOpen]);

    // Redux state and hooks
    const apiUrl = useSelector(state => state.config.api_url);

    const {
        getBalances,
        getTransactions,
        fetchTransactionOverview,
        getFeeForTransaction,
        validateEmailAddress,
    } = useJunoCommonFunctions();

    // Component state
    const [formData, setFormData] = useState({
        currency: null,
        email: '',
        amount: '',
        reference: '',
    });

    const [fieldError, setFieldError] = useState({
        currency: '',
        email: '',
        amount: '',
        reference: '',
    });

    const [transactionFee, setTransactionFee] = useState(null);
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [receiverDetails, setReceiverDetails] = useState({});

    // Status modals
    const [successModal, setSuccessModal] = useState(false);
    const [failedModal, setFailedModal] = useState({ open: false, msg: '' });

    const { readOnly } = clientData || {};

    // Initialize form data with available currencies
    useEffect(() => {
        if (!formData.currency && (balancesCurrencies || selectedBalance)) {
            const currencyValue = !isEmptyObject(selectedBalance)
                ? selectedBalance
                : balancesCurrencies?.[0];

            setFormData(prev => ({
                ...prev,
                currency: currencyValue,
            }));
        }
    }, [balancesCurrencies, selectedBalance]);

    // Handle closing the modal and resetting state
    const handleClose = (resetForm = true) => {
        setOpen(false);
        setIsLoading(false);
        setIsConfirmModal(false);

        if (resetForm) resetFormData();
    };

    // Reset form data
    const resetFormData = () => {
        const currencyValue = !isEmptyObject(selectedBalance)
            ? selectedBalance
            : balancesCurrencies?.[0];
        setFormData({
            currency: currencyValue || null,
            email: '',
            amount: '',
            reference: '',
        });

        setFieldError({
            currency: '',
            email: '',
            amount: '',
            reference: '',
        });
    };

    // Fetch fee
    const fetchFee = async () => {
        if (!formData.currency) return;

        try {
            const payload = {
                clientId: clientData.clientId,
                transactionType: 'Transfer',
                currencyType: 'fiat',
                fromCurrency: formData.currency.currencyShortName,
            };
            const fee = await getFeeForTransaction(payload);
            setTransactionFee(fee);
        } catch (error) {
            console.error('Error fetching fee:', error);
        }
    };

    // Fetch fee when currency changes
    useEffect(() => {
        if (formData.currency?.currencyShortName && clientData?.clientId) {
            fetchFee();
        }
    }, [formData.currency]);

    const handleAmountChange = (field, value) => {
        const inputValue = validateInputAmount(value);

        setFormData({
            ...formData,
            [field]: inputValue,
        });

        const validationAmountError = validateAmount(
            inputValue,
            transactionFee,
            formData.currency?.currencyShortName,
            balancesCurrencies,
        );

        setFieldError(prev => ({
            ...prev,
            [field]: validationAmountError || '',
        }));
    };

    const handleEmailChange = value => {
        setFormData({
            ...formData,
            email: value,
        });

        // Email validation
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (clientData?.clientEmail === value) {
            setFieldError(prev => ({
                ...prev,
                email: "You can't make a transaction to your email. Please, use a valid email",
            }));
        } else if (!regex.test(value)) {
            setFieldError(prev => ({
                ...prev,
                email: 'Please enter a valid email.',
            }));
        } else {
            setFieldError(prev => ({
                ...prev,
                email: '',
            }));
        }
    };

    const handleReferenceChange = value => {
        setFormData({
            ...formData,
            reference: value,
        });

        setFieldError(prev => ({
            ...prev,
            reference: '',
        }));
    };

    // Handle currency changes
    const handleCurrencyChange = currency => {
        const validationError = validateCurrency(currency?.currencyShortName, balancesCurrencies);

        setFormData({
            ...formData,
            currency: currency,
            amount: '',
        });

        setFieldError({
            ...fieldError,
            currency: validationError || '',
            amount: '',
        });
    };

    // Handle confirmation modal
    const handleConfirmationModal = async () => {
        const hasErrors = Object.values(fieldError).some(error => error !== '');
        const hasValidAmounts = formData.amount;
        const hasValidEmail = selectedMerchant || formData.email;

        if (hasValidAmounts && hasValidEmail && !hasErrors) {
            // If we're using an email (not a selected merchant), validate it with the API
            if (formData.email && !selectedMerchant) {
                setIsLoading(true);
                try {
                    const isValidEmail = await validateEmailAddress(formData.email);
                    if (!isValidEmail?.emailExist) {
                        setIsLoading(false);
                        setFieldError(prev => ({
                            ...prev,
                            email: 'No user found with this email.',
                        }));
                        return;
                    }
                    setReceiverDetails(isValidEmail);
                    // Email is valid, proceed to confirmation modal
                    if (!open) setOpen(true);
                    setIsLoading(false);
                    setIsConfirmModal(true);
                } catch (error) {
                    console.error('Error validating email:', error);
                    setFieldError(prev => ({
                        ...prev,
                        email: 'Error validating email. Please try again.',
                    }));
                    setIsLoading(false);
                }
            } else {
                // If we're using a selected merchant, no need to validate email
                setIsConfirmModal(true);
            }
        }
    };
    // Create transaction
    const handleCreateTransaction = () => {
        setIsLoading(true);

        const transactionEmail = localStorage.getItem('user_name');

        let payload = {
            currencyType: 'fiat',
            type: 'Transfer',
            currency: formData.currency.currencyShortName,
            transactionDetails: {
                email: formData.email || selectedMerchant?.clientId?.clientEmail,
                amount: formData.amount,
                reference: formData.reference,
                currency: formData.currency.currencyShortName,
            },
            client: clientData.clientId,
            transactionEmail,
            balance: {
                balanceAmount: formData.amount,
                updated: new Date(),
            },
        };

        if (selectedMerchant) {
            payload = {
                ...payload,
                transferInfee: selectedMerchant?.transferInFees,
            };
        }

        apiService(
            getClientTransactionUrl(apiUrl),
            payload,
            async data => {
                getBalances();
                handleClose(false);
                getTransactions();
                fetchTransactionOverview();
                setSuccessModal(true);
            },
            err => {
                console.error('Error creating transaction:', err);
                setFailedModal({ open: true, msg: err });
                setOpen(false);
            },
        );
    };

    // Handle modals
    const handleSuccessModal = () => {
        setSuccessModal(false);
        resetFormData();
        onCloseModal?.();
    };

    const handleFailedModal = () => {
        setFailedModal({ open: false, msg: '' });
        resetFormData();
        setIsConfirmModal(false);
        handleClose();
        onCloseModal?.();
    };

    // Create props object for child components
    const transferProps = useMemo(
        () => ({
            formData,
            fieldError,
            transactionFee,
            isLoading,
            isConfirmModal,
            balancesCurrencies,
            selectedMerchant,
            handleAmountChange,
            handleEmailChange,
            handleReferenceChange,
            handleCurrencyChange,
            setFormData,
            setFieldError,
        }),
        [
            formData,
            fieldError,
            transactionFee,
            isLoading,
            isConfirmModal,
            balancesCurrencies,
            selectedMerchant,
        ],
    );

    const hasErrors = Object.values(fieldError).some(error => error !== '');
    const hasRequiredFields = formData.amount && (selectedMerchant || formData.email);
    const isFormValid = hasRequiredFields && !hasErrors;

    return (
        <>
            {/* Standalone form when not in modal mode */}
            {!propOpen && (
                <div className="flex justify-between flex-col h-full w-full gap-3">
                    <div className="p-3 w-full h-[444px] rounded-lg border border-[#E4E4E7] dark:!border-0 bg-quickforms-bg">
                        <TransferForm {...transferProps} />
                    </div>
                    <JunoButton
                        dataE2e="rivew-transfer-btn"
                        type="widget"
                        onClick={() => {
                            if (readOnly) return;
                            handleConfirmationModal();
                        }}
                        disabled={readOnly || !isFormValid}>
                        Review Transfer
                    </JunoButton>
                </div>
            )}

            {/* Modal version */}
            <TransferModal
                {...transferProps}
                isOpen={open}
                onClose={() => {
                    handleClose();
                    onCloseModal?.();
                }}
                onBack={() => setIsConfirmModal(false)}
                handleConfirmationModal={handleConfirmationModal}
                onClickCall={handleCreateTransaction}
                isFormValid={isFormValid}
                receiverDetails={receiverDetails}
            />

            {/* Status modals */}
            {successModal && (
                <TransactionStatus
                    status="success"
                    currency={formData.currency?.currencyShortName}
                    amount={formData.amount}
                    description={
                        selectedMerchant ? (
                            <p className="font-medium text-sm uppercase text-gray-600">
                                Has been sent successfully to <br />
                                <span className="font-semibold text-sm uppercase mt-1">
                                    {receiverDetails?.username || selectedMerchant.merchantName} -
                                    Cash Transfers
                                </span>
                            </p>
                        ) : (
                            <p className="font-medium text-sm uppercase text-gray-600">
                                Has been transferred successfully
                            </p>
                        )
                    }
                    onClick={handleSuccessModal}
                    buttonText="CLOSE"
                />
            )}

            {/* Failed/Error modal */}
            {failedModal.open && (
                <TransactionStatus
                    status="error"
                    currency={formData.currency?.currencyShortName}
                    amount={formData.amount}
                    description={failedModal.msg || ''}
                    onClick={handleFailedModal}
                    buttonText="CLOSE"
                    transactionType={'TRANSFER'}
                />
            )}
        </>
    );
};

export default TransferTransaction;
